import type { CollectionEntityDefinition, ModuleContext } from '@gzl10/nexus-sdk'
import { useIdField, useSelectField, useEmailField, usePasswordField, useTextField, useDatetimeField, useCheckboxField, useImageField, useNameField, useMetadataField, useDescriptionField, useIconField } from '@gzl10/nexus-sdk/fields'
import { z } from 'zod'

const SYSTEM_ROLE_NAMES = ['ADMIN', 'EDITOR', 'USER', 'VIEWER', 'SUPPORT'] as const

/**
 * EntityDefinition for Users
 *
 * Single source of truth for:
 * - Knex migrations (generateMigration)
 * - Zod schemas (generateZodSchema)
 * - TypeScript types (generateModel)
 * - CASL permissions (generateCaslPermissions)
 */
export const userEntity: CollectionEntityDefinition = {
  type: "collection",
  realtime: 'sync',
  table: "users",
  label: { en: "User", es: "Usuario" },
  labelPlural: { en: "Users", es: "Usuarios" },
  labelField: "name",
  timestamps: true,
  audit: true,
  attachable: { accept: 'image/*,application/pdf', maxFiles: 10 },
  order: 1,
  routePrefix: "/", // Monta en raíz del módulo: /users/*

  fields: {
    id: useIdField(),
    email: useEmailField({
      label: { en: "Email", es: "Correo electrónico" },
      placeholder: { en: "user@example.com", es: "usuario@ejemplo.com" },
      nullable: true,
      unique: true,
      meta: { sortable: true, searchable: true },
    }),
    password: usePasswordField({
      label: { en: "Password", es: "Contraseña" },
      placeholder: "••••••••",
      nullable: true,
      validation: { min: 6 },
      meta: { exportable: false, showInDisplay: false, showInForm: "create" },
    }),
    name: useNameField({
      size: 255,
      placeholder: { en: 'Full name', es: 'Nombre completo' },
      validation: { min: 1 }
    }),
    metadata: useMetadataField(),
    avatar: {
      ...useImageField({
        label: { en: "Avatar", es: "Avatar" },
        nullable: true,
        meta: { exportable: false },
      }),
      displayProps: { order: 1, width: 80 },
      relation: { table: "storage_files", column: "id", onDelete: "SET NULL" },
      storage: {
        accept: "image/*",
        maxSize: 2 * 1024 * 1024,
        folder: "avatars",
        thumbnails: [{ width: 128, height: 128 }],
        dedupe: true,
      },
    },

    // GDPR/Privacy consent fields
    consent_date: useDatetimeField({
      label: { en: "Consent Date", es: "Fecha de consentimiento" },
      hidden: true,
      nullable: true,
      meta: { exportable: true, showInForm: false, showInDisplay: false },
    }),
    consent_version: useTextField({
      label: { en: "Consent Version", es: "Versión de consentimiento" },
      hidden: true,
      size: 20,
      nullable: true,
      meta: { exportable: true, showInForm: false, showInDisplay: false },
    }),
    marketing_opt_in: useCheckboxField({
      label: { en: "Marketing Opt-in", es: "Aceptar marketing" },
      meta: { exportable: true, showInForm: false, showInDisplay: false },
    }),
    locale: useSelectField({
      label: { en: "Language", es: "Idioma" },
      options: [
        { value: "es", label: { en: "Spanish", es: "Español" } },
        { value: "en", label: { en: "English", es: "Inglés" } },
      ],
      nullable: true,
      meta: { sortable: true },
      defaultValue: 'en'
    }),
    timezone: useSelectField({
      label: { en: "Timezone", es: "Zona horaria" },
      master: "timezones",
      meta: { sortable: true },
      defaultValue: "timezones:Europe/Madrid",
    }),
    type: useSelectField({
      label: { en: "Type", es: "Tipo" },
      defaultValue: "human",
      options: [
        {
          value: "human",
          label: { en: "Human", es: "Humano" },
          icon: "mdi:account",
        },
        { value: "bot", label: { en: "Bot", es: "Bot" }, icon: "mdi:robot" },
        {
          value: "service",
          label: { en: "Service", es: "Servicio" },
          icon: "mdi:cog",
        },
      ],
      meta: { sortable: true, searchable: false },
    }),
    // Campo virtual para gestionar roles (no va a DB, se procesa en service)
    role_ids: {
      label: { en: "Roles", es: "Roles" },
      input: "transfer",
      db: { type: "array", virtual: true },
      options: {
        endpoint: "/users/roles",
        valueField: "id",
        labelField: "name",
      },
      meta: { showInDisplay: true, showInForm: true },
    },
  },

  // Entity Actions
  actions: [
    {
      key: "change-password",
      label: { en: "Change Password", es: "Cambiar contraseña" },
      icon: "mdi:lock-reset",
      method: "POST",
      select: ["id", "password"],
      confirm: {
        type: "simple",
        message: {
          en: "You are about to change the password for this user.",
          es: "Vas a cambiar la contraseña de este usuario.",
        },
        severity: "warning",
      },
      input: {
        newPassword: {
          input: "password",
          required: true,
          label: { en: "New Password", es: "Nueva contraseña" },
          hint: { en: "Minimum 6 characters", es: "Mínimo 6 caracteres" },
        },
        currentPassword: {
          input: "password",
          label: { en: "Current Password", es: "Contraseña actual" },
          hint: {
            en: "Required only when changing your own password",
            es: "Requerido solo al cambiar tu propia contraseña",
          },
        },
      },
      inputSchema: z.object({
        currentPassword: z
          .string()
          .optional()
          .transform((v) => v || undefined),
        newPassword: z
          .string()
          .min(6, "Password must be at least 6 characters"),
      }),
      middleware: (ctx: ModuleContext) =>
        ctx.core.middleware.rateLimit({
          windowMs: 15 * 60 * 1000,
          max: 5,
          message: 'Too many attempts, try again in 15 minutes',
        }),
      handler: async (ctx: ModuleContext, input: unknown) => {
        const {
          _record: user,
          _authUserId,
          currentPassword,
          newPassword,
        } = input as {
          _record: { id: string; password: string };
          _authUserId?: string;
          currentPassword?: string;
          newPassword: string;
        };
        const { verifyPassword, hashPassword } = ctx.core.crypto;

        // Si el usuario cambia SU PROPIA contraseña, debe proporcionar currentPassword
        const isOwnPassword = _authUserId === user.id;
        if (isOwnPassword) {
          if (!currentPassword) {
            throw new ctx.core.errors.ValidationError(
              "Current password is required when changing your own password",
            );
          }
          const isValid = await verifyPassword(currentPassword, user.password);
          if (!isValid) {
            throw new ctx.core.errors.UnauthorizedError(
              "Current password is incorrect",
            );
          }
        }
        // Si ADMIN cambia la de otro usuario, no se verifica currentPassword
        // (CASL ya verifica que tenga permisos de update sobre User)

        // Hash y actualizar
        const hashedPassword = await hashPassword(newPassword);
        await ctx.db
          .knex("users")
          .where("id", user.id)
          .update({
            password: hashedPassword,
            updated_at: ctx.db.nowTimestamp(ctx.db.knex),
          });

        return { updated: true };
      },
      casl: { action: "change-password" },
    },
  ],

  // Autorización CASL ('@' = cualquier usuario autenticado puede leer/actualizar su propio perfil)
  casl: {
    subject: "User",
    sensitiveFields: ["password"],
    permissions: {
      "@": [
        { actions: ["read"], conditions: { id: "${user.id}" } },
        {
          actions: ["update"],
          conditions: { id: "${user.id}" },
          fields: ["name", "avatar", "locale", "timezone"],
        },
        { actions: ["change-password"], conditions: { id: "${user.id}" } },
      ],
      SUPPORT: { actions: ["read"] }
    },
  },
};

/**
 * EntityDefinition for Roles
 */
export const roleEntity: CollectionEntityDefinition = {
  type: 'collection',
  realtime: 'sync',
  table: "roles",
  label: { en: "Role", es: "Rol" },
  labelPlural: { en: "Roles", es: "Roles" },
  labelField: "name",
  timestamps: true,
  order: 10,
  routePrefix: "/roles", // Monta en /users/roles/*
  defaultSort: { field: 'name', order: 'asc' },

  fields: {
    id: useIdField(),
    icon: useIconField({
      label: { en: 'Icon', es: 'Icono' },
      placeholder: 'mdi:shield-account'
    }),
    name: useNameField({
      size: 50,
      disabled: true,
      placeholder: "ROLE_NAME",
      unique: true,
      validation: { min: 2, max: 50, pattern: "^[A-Z_]+$" },
      meta: { searchable: true, sortable: true }
    }),
    description: useDescriptionField({ localized: true, searchable: true })
  },

  // Protege roles del sistema contra borrado
  hooks: (ctx) => ({
    beforeDelete: async (id: string) => {
      const row = await ctx.db.knex('roles').where({ id }).first<{ name: string }>()
      if (row && (SYSTEM_ROLE_NAMES as readonly string[]).includes(row.name)) {
        throw new ctx.core.errors.ForbiddenError(`System role "${row.name}" cannot be deleted`)
      }
    }
  }),

  casl: {
    subject: "Role",
    permissions: {
      '@': [{ actions: ['read'] }]
    },
  },
};

/**
 * EntityDefinition for User Roles (pivote many-to-many)
 */
export const userRoleEntity: CollectionEntityDefinition = {
  type: 'collection',
  realtime: 'sync',
  table: 'user_roles',
  label: { en: 'User Role', es: 'Rol de Usuario' },
  labelPlural: { en: 'User Roles', es: 'Roles de Usuario' },
  labelField: 'role_id',
  timestamps: true,
  order: 15,
  hidden: true, // Pivot table - managed via Users UI
  expose: false,

  fields: {
    id: useIdField(),
    user_id: useSelectField({
      label: { en: 'User', es: 'Usuario' },
      required: true,
      table: 'users',
      column: 'id',
      onDelete: 'CASCADE',
      endpoint: '/users',
      valueField: 'id',
      labelField: 'name',
      meta: { searchable: true }
    }),
    role_id: useSelectField({
      label: { en: 'Role', es: 'Rol' },
      required: true,
      table: 'roles',
      column: 'id',
      onDelete: 'CASCADE',
      endpoint: '/users/roles',
      valueField: 'id',
      labelField: 'name',
      meta: { searchable: true }
    })
  },

  indexes: [
    { columns: ['user_id', 'role_id'], unique: true }
  ],

  casl: {
    subject: 'UserRole',
    permissions: {
      // Solo ADMIN puede gestionar asignaciones (via manage all global)
    }
  }
}
